import React from 'react';
import { Box, Typography, TextField, IconButton, Button, Popover } from '@mui/material';
import {
  Add,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { EditIcon, DeleteIcon } from '../shared/Icons';
import { ADMIN_COLORS } from '../../theme';

/* Docked to the BOTTOM of the builder, below the canvas — a second strip under
   the app header read as two stacked headers and pushed the canvas down. Numbered
   chips + a trailing count mirror the page-switcher pattern users know from other
   form tools; the palette stays ours (indigo/grey), not the reference's green. */

/* Exported so FormBuilder can offset the fixed side rails by exactly this much —
   the rails are `position: fixed; bottom: 0` and would otherwise run under it. */
export const STEP_BAR_HEIGHT = 52;

interface StepBarProps {
  steps: string[];
  currentStep: number;
  editingStepIndex: number | null;
  editingStepName: string;
  deleteAnchorEl: HTMLElement | null;
  stepToDelete: number | null;
  onSelectStep: (index: number) => void;
  onAddStep: () => void;
  onStartEditStep: (index: number) => void;
  onCancelEditStep: () => void;
  onEditingStepNameChange: (value: string) => void;
  onCommitStepName: (index: number, name: string) => void;
  onRequestDelete: (anchor: HTMLElement, index: number) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (index: number) => void;
}

export const StepBar: React.FC<StepBarProps> = ({
  steps,
  currentStep,
  editingStepIndex,
  editingStepName,
  deleteAnchorEl,
  stepToDelete,
  onSelectStep,
  onAddStep,
  onStartEditStep,
  onCancelEditStep,
  onEditingStepNameChange,
  onCommitStepName,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}) => (
  <Box
    sx={{
      flexShrink: 0,
      height: STEP_BAR_HEIGHT,
      backgroundColor: ADMIN_COLORS.surface,
      borderTop: '1px solid',
      borderColor: 'divider',
      px: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      zIndex: 1100,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        // The scrollbar would sit on the bar's own 48px height and clip the
        // chips, so it is hidden — the chips scroll by drag/wheel instead.
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {steps.map((stepName, index) => {
        const isEditing = editingStepIndex === index;
        const isActive = currentStep === index;

        if (isEditing) {
          return (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
            >
              <TextField
                value={editingStepName}
                onChange={(e) => onEditingStepNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onCommitStepName(index, editingStepName);
                  else if (e.key === 'Escape') onCancelEditStep();
                }}
                size="small"
                autoFocus
                sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '0.8125rem', height: 32 } }}
              />
              <IconButton
                size="small"
                onClick={() => onCommitStepName(index, editingStepName)}
                sx={{ color: 'success.main' }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onCancelEditStep} sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }

        return (
          <Box
            key={index}
            role="button"
            onClick={() => onSelectStep(index)}
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              height: 34,
              pr: isActive ? 0.5 : 0,
              borderRadius: '8px',
              cursor: 'pointer',
              overflow: 'hidden',
              fontSize: '0.8125rem',
              fontWeight: 500,
              backgroundColor: ADMIN_COLORS.surface,
              border: '1px solid',
              borderColor: isActive ? ADMIN_COLORS.accent : ADMIN_COLORS.border,

              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                borderColor: isActive ? 'primary.main' : ADMIN_COLORS.borderStrong,
              },
              '&:hover .step-actions': { opacity: 1 },
            }}
          >
            {/* Ordinal in its own compartment, split off by a divider — the step's
                position is what users scan for, and a renamed step otherwise hides
                its order. Plain text, not a filled badge: a solid swatch per chip
                turned the bar into a row of coloured blocks. */}
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                px: 1.125,
           
                borderTopLeftRadius: '7px',
                borderBottomLeftRadius: '7px',
                borderRight: '1px solid',
                borderColor: isActive ? ADMIN_COLORS.accentRing : ADMIN_COLORS.border,
                backgroundColor: isActive ? ADMIN_COLORS.accentSoft : ADMIN_COLORS.headBg,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isActive ? 'primary.main' : 'text.disabled',
              }}
            >
              {index + 1}
            </Box>

            <Box
              component="span"
              sx={{
                px: 1.25,
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: isActive ? 'primary.main' : 'text.primary',
                fontWeight: isActive ? 600 : 500,
              }}
            >
              {stepName}
            </Box>

            {isActive && (
              <Box
                className="step-actions"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  opacity: { xs: 1, sm: 0.6 },
                  transition: 'opacity 0.15s ease',
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartEditStep(index);
                  }}
                  sx={{ p: 0.375, color: 'inherit' }}
                >
                  <EditIcon size={14} />
                </IconButton>
                {steps.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete(e.currentTarget, index);
                    }}
                    sx={{ p: 0.375, color: 'error.main' }}
                  >
                    <DeleteIcon size={14} />
                  </IconButton>
                )}
              </Box>
            )}

            <Popover
              open={deleteAnchorEl !== null && stepToDelete === index}
              anchorEl={deleteAnchorEl}
              onClose={onCancelDelete}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, minWidth: 240 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Delete this step?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={onCancelDelete}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={() => onConfirmDelete(index)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Popover>
          </Box>
        );
      })}
    </Box>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexShrink: 0,
        pl: 1.5,
        borderLeft: '1px solid',
        borderColor: 'divider',
      }}
    >
      <IconButton
        onClick={onAddStep}
        title="Add step"
        aria-label="Add step"
        sx={{
          width: 30,
          height: 30,
          color: '#ffffff',
          backgroundColor: 'primary.main',
          boxShadow: `0 1px 3px ${ADMIN_COLORS.accentRing}`,
          '&:hover': { backgroundColor: 'primary.dark' },
        }}
      >
        <Add sx={{ fontSize: 18 }} />
      </IconButton>

      <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 600 }}>
          Steps
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>
          {steps.length}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default StepBar;
